import React from "react";
import { adaptivePercentTicks, chartRatio, chartLabelIndexes } from "../utils/dashboardChartGeometry.js";
import "./DashboardCharts.css";
import FleetUtilizationCard from "./FleetUtilizationCard.jsx";
import DashboardKpiStrip from "./DashboardKpiStrip.jsx";
import DashboardWatchList from "./DashboardWatchList.jsx";
import DashboardEconomicsCard from "./DashboardEconomicsCard.jsx";
import DashboardMetricList from "./DashboardMetricList.jsx";
import DashboardCalculationHover, { DashboardCalculationHint, DASHBOARD_CALCULATIONS } from "./DashboardCalculationHover.jsx";
import { LayoutDashboard } from "lucide-react";
import WorkspacePageHeader from "./WorkspacePageHeader.jsx";
import { HoverCard as DashboardHoverCard } from "./ResultsPrimitives.jsx";
import "./MobileWorkspace.css";
import useDashboardHomeData from "../hooks/useDashboardHomeData.js";
import { summarizeAnalyticsPeriodDays } from "../utils/dashboardHomeData.js";
import { resolvePageSurfaceWidthClassName } from "../utils/pageSurfaceLayout.js";
import { joinClasses, pillToneClasses } from "./resultsPrimitives/shared.js";
import AppLoadingBanner from "./AppLoadingBanner.jsx";
import BrandLogo from "./BrandLogo.jsx";
import DashboardReportActions, { DashboardIndicatorContext, DashboardReportContext } from "./DashboardReportActions.jsx";
import { DASHBOARD_REPORT_BY_KEY } from "../utils/dashboardReportCatalog.js";

const DASHBOARD_SURFACE_WIDTH_CLASS_NAME = resolvePageSurfaceWidthClassName("dashboard");
function HoverCard(props) {
  return <DashboardHoverCard {...props} openOnTouch />;
}
const LINE_PRIMARY_COLOR = "var(--color-accent)";
const LINE_SECONDARY_COLOR = "var(--color-slate-400, #94a3b8)";
const GRID_COLOR = "var(--color-slate-200, #e2e8f0)";

const DASHBOARD_DATE_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});
const COMPACT_NUMBER_FORMATTER = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});
const INTEGER_NUMBER_FORMATTER = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const TONNAGE_NUMBER_FORMATTER = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

function clamp(value, minimum, maximum) {
  return Math.min(Math.max(value, minimum), maximum);
}

function toChartNumber(value, fallback = 0) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
}

function buildChartPoints(
  items,
  valueAccessor,
  {
    bottomPadding = 28,
    height = 220,
    maxValue = 100,
    minValue = 0,
    topPadding = 18,
    width = 520,
    xStart = 46,
  } = {},
) {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  const safeMaxValue = maxValue <= minValue ? minValue + 1 : maxValue;
  const plotHeight = Math.max(height - topPadding - bottomPadding, 1);
  const step = items.length > 1 ? width / (items.length - 1) : 0;

  return items.map((item, index) => {
    const rawValue = clamp(toChartNumber(valueAccessor(item), 0), minValue, safeMaxValue);
    const ratio = (rawValue - minValue) / (safeMaxValue - minValue);
    return {
      item,
      value: rawValue,
      x: xStart + (items.length > 1 ? index * step : width / 2),
      y: topPadding + ((1 - ratio) * plotHeight),
    };
  });
}

function buildLinePath(points = []) {
  if (!points.length) {
    return "";
  }

  return points.map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`).join(" ");
}

function buildAreaPath(points = [], baseY = 0) {
  if (!points.length) {
    return "";
  }

  const commands = [
    `M ${points[0].x} ${baseY}`,
    `L ${points[0].x} ${points[0].y}`,
    ...points.slice(1).map((point) => `L ${point.x} ${point.y}`),
    `L ${points[points.length - 1].x} ${baseY}`,
    "Z",
  ];

  return commands.join(" ");
}

const EMPTY_PERIOD = Object.freeze({
  availableDispatchCount: 0,
  availableSnapshotCount: 0,
  days: [],
  hasHistory: false,
  historyState: "empty",
  key: "month",
  label: "30 dispatchs actifs",
  shortLabel: "30 dispatchs",
});

function resolveHistoryPresentation(period = EMPTY_PERIOD) {
  const resolvedDays = Array.isArray(period.days) ? period.days : [];

  if (period.key === "day") {
    if (resolvedDays.some((day) => day?.hasDispatch)) {
      return {
        className: pillToneClasses.success,
        label: "Hier",
      };
    }

    if (resolvedDays.some((day) => day?.hasSnapshot)) {
      return {
        className: pillToneClasses.warning,
        label: "Jour partiel",
      };
    }

    return {
      className: pillToneClasses.neutral,
      label: "Jour sans dispatch",
    };
  }

  if (period.historyState === "ready") {
    return {
      className: pillToneClasses.success,
      label: `${period.shortLabel} actifs`,
    };
  }

  if (period.historyState === "partial") {
    return {
      className: pillToneClasses.warning,
      label: `${period.availableDispatchCount} dispatchs actifs`,
    };
  }

  return {
    className: pillToneClasses.neutral,
    label: `${period.shortLabel} indisponibles`,
  };
}

function resolveVisibleLabelIndexes(length, targetCount = 7) {
  if (!Number.isFinite(length) || length <= 0) {
    return new Set();
  }

  if (length <= targetCount) {
    return new Set(Array.from({ length }, (_value, index) => index));
  }

  const step = Math.max(Math.ceil((length - 1) / (targetCount - 1)), 1);
  const indexes = new Set([0, length - 1]);

  for (let index = 0; index < length; index += step) {
    indexes.add(index);
  }

  return indexes;
}

function resolveAxisLabel(day, periodKey) {
  if (periodKey === "month") {
    return day?.dateLabel || day?.dayLabel || "";
  }

  return day?.dayLabel || day?.dateLabel || "";
}

function resolveCurrentPeriodDay(period = EMPTY_PERIOD) {
  if (!Array.isArray(period.days) || !period.days.length) {
    return null;
  }

  return period.days[period.days.length - 1] || null;
}

function formatDurationLabel(minutes) {
  const numericMinutes = Math.round(Math.max(toChartNumber(minutes, 0), 0));
  const hours = Math.floor(numericMinutes / 60);
  const remainingMinutes = Math.round(numericMinutes - (hours * 60));

  if (hours <= 0) {
    return `${remainingMinutes} min`;
  }

  if (remainingMinutes <= 0) {
    return `${hours}h`;
  }

  return `${hours}h${String(remainingMinutes).padStart(2, "0")}`;
}

function formatPercentLabel(value) {
  return `${COMPACT_NUMBER_FORMATTER.format(Math.max(toChartNumber(value, 0), 0))} %`;
}

function formatCountLabel(value) {
  const numericValue = Math.max(toChartNumber(value, 0), 0);

  if (Math.abs(numericValue - Math.round(numericValue)) < 0.05) {
    return INTEGER_NUMBER_FORMATTER.format(Math.round(numericValue));
  }

  return COMPACT_NUMBER_FORMATTER.format(numericValue);
}

function formatTonnageLabel(value) {
  return `${TONNAGE_NUMBER_FORMATTER.format(Math.max(toChartNumber(value, 0), 0))} t`;
}

function formatDistanceLabel(value) {
  return `${COMPACT_NUMBER_FORMATTER.format(Math.max(toChartNumber(value, 0), 0))} km`;
}

function formatCostPerUnitLabel(value, unit, digits = 2) {
  if (value == null || !Number.isFinite(Number(value))) return "n/d";
  return `${new Intl.NumberFormat("fr-CA", { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(Number(value))} $/${unit}`;
}

function formatCurrencyLabel(value) {
  const numericValue = value == null ? Number.NaN : Number(value);
  if (!Number.isFinite(numericValue)) {
    return "n/d";
  }

  return new Intl.NumberFormat("fr-CA", {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numericValue);
}


function resolveRoundedTonnageAxisStep(maxValue) {
  const safeMaxValue = Math.max(toChartNumber(maxValue, 0), 0);

  if (safeMaxValue <= 0) {
    return 100;
  }

  return Math.max(Math.ceil((safeMaxValue / 4) / 100) * 100, 100);
}

function resolvePeriodCoverageLabel(period = EMPTY_PERIOD, summary = null) {
  if (period.reportScope) return period.reportScope;
  if (period.key === "day") {
    const currentDay = resolveCurrentPeriodDay(period);

    if (currentDay?.dispatchDate) {
      return currentDay.hasDispatch
        ? `Hier · ${currentDay.dispatchDate}`
        : `${currentDay.dispatchDate} · aucun dispatch`;
    }

    return currentDay?.hasDispatch ? "Hier" : "Aucun dispatch hier";
  }

  const totalDays = Array.isArray(period.days) && period.days.length
    ? period.days.length
    : summary?.totalDaysCount || 0;
  const availableDays = period.availableDispatchCount || summary?.dispatchDayCount || 0;

  if (!totalDays && !availableDays) {
    return "Aucun dispatch actif lu";
  }

  if (period.key === "week") {
    return `${availableDays} dispatch${availableDays > 1 ? "s" : ""} actifs récents`;
  }

  return `${availableDays} dispatch${availableDays > 1 ? "s" : ""} actifs analysés`;
}

function resolveAnalyticsMetricCoverageLabel(period = EMPTY_PERIOD, summary = null) {
  const totalDays = Number(summary?.totalDaysCount ?? (Array.isArray(period.days) ? period.days.length : 0));
  const validDays = Number(summary?.validDayCount ?? summary?.sampleCount ?? 0);
  const dispatchDays = Number(summary?.dispatchDayCount ?? period.availableDispatchCount ?? 0);

  if (!Number.isFinite(totalDays) || totalDays <= 0) {
    return "Aucune journée valide";
  }

  return `${Math.max(validDays, 0)} / ${Math.max(totalDays, 0)} journées valides · ${Math.max(dispatchDays, 0)} dispatch${dispatchDays > 1 ? "s" : ""} actif${dispatchDays > 1 ? "s" : ""}`;
}

function resolvePlannedAnalyticsNote(period = EMPTY_PERIOD, summary = null) {
  return `Données planifiées du dispatch, pas une télémétrie. ${resolveAnalyticsMetricCoverageLabel(period, summary)}.`;
}

function resolveLowLoadCoverageLabel(period = EMPTY_PERIOD, summary = null) {
  const validDays = Number(summary?.lowLoadValidDayCount ?? 0);
  const dispatchDays = Number(summary?.dispatchDayCount ?? period.availableDispatchCount ?? 0);

  if (!Number.isFinite(dispatchDays) || dispatchDays <= 0) {
    return "Aucun jour avec distance classifiée";
  }

  return `${Math.max(validDays, 0)} / ${Math.max(dispatchDays, 0)} jours avec distance classifiée`;
}


function normalizeDashboardText(value) {
  return typeof value === "string" && value.trim() ? value.trim() : "";
}

function parseDashboardHoverDate(value) {
  if (typeof value !== "string" || !value.trim()) {
    return null;
  }

  const parsedDate = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00` : value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

function formatAnalyticsHoverDateLabel(day = null) {
  const parsedDate = parseDashboardHoverDate(day?.dispatchDate);

  if (parsedDate instanceof Date) {
    return DASHBOARD_DATE_FORMATTER.format(parsedDate);
  }

  return normalizeDashboardText(day?.dispatchDate) || normalizeDashboardText(day?.dateLabel) || "Date indisponible";
}

function formatOrdersCountLabel(value) {
  const orderCount = Math.max(Math.round(toChartNumber(value, 0)), 0);
  return `${INTEGER_NUMBER_FORMATTER.format(orderCount)} commande${orderCount > 1 ? "s" : ""}`;
}

function resolveDayAnalyticsNote(day = null) {
  if (day?.hasDispatch) {
    return `${formatOrdersCountLabel(day.dispatchOrderCount)} prises en compte dans ce dispatch actif.`;
  }

  return "Aucun dispatch actif exploitable pour cette journée. Elle reste exclue des moyennes.";
}

function DashboardHoverCardContent({ eyebrow = "", note = "", rows = [] }) {
  return (
    <div className="grid gap-2">
      {eyebrow ? (
        <p className="text-[11px] font-semibold text-slate-700">{eyebrow}</p>
      ) : null}
      <div className="grid gap-1.5">
        {rows.filter((row) => row && row.value).map((row) => (
          <div key={row.label} className="flex items-start justify-between gap-3 text-[11px] leading-4">
            <span className="text-slate-700">{row.label}</span>
            <span className="text-right font-semibold text-[rgb(31,44,39)]">{row.value}</span>
          </div>
        ))}
      </div>
      {note ? (
        <p className="border-t border-[rgba(100,116,106,0.12)] pt-2 text-[11px] leading-4 text-slate-700">{note}</p>
      ) : null}
    </div>
  );
}

export function buildPeriodAnalyticsSummary(period = EMPTY_PERIOD, fallbackDay = null) {
  const rawSummary = summarizeAnalyticsPeriodDays(period.days, { fallbackDay: period.key === "day" ? null : fallbackDay });

  if (!rawSummary.hasData) {
    const emptyNote = rawSummary.dispatchDayCount > 0
      ? "Aucune métrique exploitable pour les jours avec dispatch"
      : "Aucun dispatch sur la période";

    return {
      ...rawSummary,
      avgAssignedDurationLabel: "n/d",
      avgBusinessStopCountLabel: "n/d",
      avgCompartmentFillLabel: "n/d",
      avgCompartmentUsageLabel: "n/d",
      avgCompartmentsUsedLabel: "n/d",
      avgDispatchOrderCountLabel: "n/d",
      avgDistancePerUsedTruckLabel: "n/d",
      avgHighLoadDriveShareLabel: "n/d",
      avgMaxWorkLabel: null,
      avgPeakCompartmentOccupancyLabel: "n/d",
      avgPeakCompartmentsUsedLabel: "n/d",
      avgPeakLoadFillRateLabel: "n/d",
      avgPlannedTonnageLabel: "n/d",
      avgTonsPerDeliveryStopLabel: "n/d",
      avgTimeBudgetConsumedLabel: "n/d",
      avgTonnagePerUsedTruckLabel: "n/d",
      avgFleetUsageLabel: "n/d",
      avgTruckOccupancyLabel: "n/d",
      fleetWorkedLabel: "n/d",
      fleetMaxWorkLabel: "n/d",
      fleetTimeUtilizationLabel: "n/d",
      fleetUnproductiveLabel: "n/d",
      avgDeliveryVisitCountPerActiveTruckLabel: "n/d",
      lowLoadDistanceLabel: "n/d",
      classifiedDistanceLabel: "n/d",
      lowLoadDistancePctLabel: "n/d",
      loadedDistancePctLabel: "n/d",
      availableCompartmentsLabel: "n/d",
      compartmentFillNote: emptyNote,
      compartmentFillRateLabel: "n/d",
      compartmentUsageNote: emptyNote,
      compartmentNote: emptyNote,
      dispatchEstimatedCostLabel: "n/d",
      realCostPerDispatchLabel: "n/d",
      realCostPerTonneLabel: "n/d",
      realCostPerOrderLabel: "n/d",
      realCostPerHourLabel: "n/d",
      dispatchProductCountLabel: "n/d",
      gaugeRatio: 0,
      preloadTruckLabel: "n/d",
      priorityWindowTruckLabel: "n/d",
      protectedOrderLabel: "n/d",
      totalCompartmentsLabel: "n/d",
      totalPlannedTonnageLabel: "n/d",
      usedCompartmentsLabel: "n/d",
      usedFleetAverageNote: emptyNote,
      usedFleetLabel: "n/d",
    };
  }

  const {
    activeTruckCount,
    avgAssignedDurationMinutes,
    avgBusinessStopCountPerUsedTruck,
    avgCompartmentFillPct,
    avgCompartmentUsagePct,
    avgCompartmentsUsed,
    avgDispatchOrderCount,
    avgDistancePerUsedTruckKm,
    avgHighLoadDriveSharePct,
    avgMaxWorkMinutes,
    avgPeakCompartmentOccupancyPct,
    avgPeakCompartmentsUsed,
    avgPeakLoadFillRatePct,
    avgPlannedTonnageTons,
    avgTonsPerDeliveryStopTons,
    avgTimeBudgetConsumedPct,
    avgTonnagePerUsedTruckTons,
    avgFleetUsagePct,
    avgTruckOccupancyPct,
    fleetWorkedMinutes,
    fleetMaxWorkMinutes,
    fleetTimeUtilizationPct,
    fleetUnproductiveMinutes,
    fleetUnproductivePct,
    deliveryVisitCount,
    avgDeliveryVisitCountPerActiveTruck,
    lowLoadDistanceKm,
    classifiedDistanceKm,
    lowLoadDistancePct,
    lowLoadThresholdPct,
    lowLoadValidDayCount,
    totalActiveTruckCount,
    availableCompartments,
    compartmentFillRatio,
    dispatchEstimatedCostCad,
    dispatchProductCount,
    preloadTruckCount,
    preloadTruckPct,
    priorityWindowTruckCount,
    priorityWindowTruckPct,
    protectedOrderCount,
    protectedOrderPct,
    protectedOrderTotalCount,
    totalCompartments,
    totalPlannedTonnageTons,
    usedCompartments,
    usedFleetPct,
    usedTruckCount,
  } = rawSummary;

  return {
    ...rawSummary,
    activeTruckCount,
    avgAssignedDurationLabel: formatDurationLabel(avgAssignedDurationMinutes),
    avgAssignedDurationMinutes,
    avgBusinessStopCountLabel: formatCountLabel(avgBusinessStopCountPerUsedTruck),
    avgBusinessStopCountPerUsedTruck,
    avgCompartmentFillLabel: avgCompartmentFillPct !== null ? formatPercentLabel(avgCompartmentFillPct) : "n/d",
    avgCompartmentFillPct,
    avgCompartmentUsageLabel: avgCompartmentUsagePct !== null ? formatPercentLabel(avgCompartmentUsagePct) : "n/d",
    avgCompartmentUsagePct,
    avgCompartmentsUsed,
    avgCompartmentsUsedLabel: avgCompartmentsUsed !== null ? formatCountLabel(avgCompartmentsUsed) : "n/d",
    avgDispatchOrderCount,
    avgDispatchOrderCountLabel: formatCountLabel(avgDispatchOrderCount),
    avgDistancePerUsedTruckLabel: formatDistanceLabel(avgDistancePerUsedTruckKm),
    avgDistancePerUsedTruckKm,
    avgHighLoadDriveShareLabel: formatPercentLabel(avgHighLoadDriveSharePct),
    avgHighLoadDriveSharePct,
    avgMaxWorkLabel: avgMaxWorkMinutes > 0 ? formatDurationLabel(avgMaxWorkMinutes) : null,
    avgMaxWorkMinutes,
    avgPeakCompartmentOccupancyLabel: formatPercentLabel(avgPeakCompartmentOccupancyPct),
    avgPeakCompartmentOccupancyPct,
    avgPeakCompartmentsUsed,
    avgPeakCompartmentsUsedLabel: formatCountLabel(avgPeakCompartmentsUsed),
    avgPeakLoadFillRateLabel: avgPeakLoadFillRatePct === null ? "n/d" : formatPercentLabel(avgPeakLoadFillRatePct),
    avgPeakLoadFillRatePct,
    avgPlannedTonnageLabel: formatTonnageLabel(avgPlannedTonnageTons),
    avgTonsPerDeliveryStopLabel: formatTonnageLabel(avgTonsPerDeliveryStopTons),
    avgTonsPerDeliveryStopTons,
    avgTimeBudgetConsumedLabel: formatPercentLabel(avgTimeBudgetConsumedPct),
    avgTimeBudgetConsumedPct,
    avgTonnagePerUsedTruckLabel: formatTonnageLabel(avgTonnagePerUsedTruckTons),
    avgTonnagePerUsedTruckTons,
    avgFleetUsageLabel: formatPercentLabel(avgFleetUsagePct),
    avgFleetUsagePct,
    availableCompartments,
    availableCompartmentsLabel: formatCountLabel(availableCompartments),
    compartmentFillNote: avgCompartmentFillPct !== null
      ? "Moyenne observée sur les compartiments mobilisés"
      : "Remplissage moyen non disponible sur la période",
    compartmentFillRateLabel: avgCompartmentUsagePct !== null ? formatPercentLabel(avgCompartmentUsagePct) : "n/d",
    compartmentFillRatio,
    compartmentUsageNote: avgCompartmentsUsed !== null
      ? `${formatCountLabel(avgCompartmentsUsed)} compartiments utilisés en moyenne par camion`
      : "Aucun compartiment actif sur la période",
    compartmentNote: avgCompartmentsUsed !== null
      ? `${formatCountLabel(avgCompartmentsUsed)} compartiments utilisés en moyenne par camion`
      : "Aucun compartiment actif sur la période",
    dispatchEstimatedCostCad,
    dispatchEstimatedCostLabel: formatCurrencyLabel(dispatchEstimatedCostCad),
    realCostPerDispatchLabel: formatCurrencyLabel(rawSummary.realCostPerDispatchCad),
    realCostPerTonneLabel: formatCostPerUnitLabel(rawSummary.realCostPerTonneCad, "t"),
    realCostPerOrderLabel: formatCurrencyLabel(rawSummary.realCostPerOrderCad),
    realCostPerHourLabel: formatCostPerUnitLabel(rawSummary.realCostPerHourCad, "h", 0),
    dispatchProductCount,
    dispatchProductCountLabel: formatCountLabel(dispatchProductCount),
    gaugeRatio: avgMaxWorkMinutes > 0
      ? clamp(avgAssignedDurationMinutes / avgMaxWorkMinutes, 0, 1)
      : clamp(avgTimeBudgetConsumedPct / 100, 0, 1),
    preloadTruckCount,
    preloadTruckLabel: formatPercentLabel(preloadTruckPct),
    preloadTruckPct,
    priorityWindowTruckCount,
    priorityWindowTruckLabel: formatPercentLabel(priorityWindowTruckPct),
    priorityWindowTruckPct,
    protectedOrderCount,
    protectedOrderLabel: formatPercentLabel(protectedOrderPct),
    protectedOrderPct,
    protectedOrderTotalCount,
    sampleCount: rawSummary.sampleCount,
    totalCompartments,
    totalCompartmentsLabel: formatCountLabel(totalCompartments),
    totalPlannedTonnageLabel: formatTonnageLabel(totalPlannedTonnageTons),
    usedCompartments,
    usedCompartmentsLabel: formatCountLabel(usedCompartments),
    usedFleetAverageNote: activeTruckCount > 0
      ? `${formatCountLabel(activeTruckCount)} camions actifs en moyenne · ${formatCountLabel(usedTruckCount)} avec commandes`
      : "Aucun camion actif sur la période",
    usedFleetLabel: formatPercentLabel(usedFleetPct),
    avgTruckOccupancyLabel: formatPercentLabel(avgTruckOccupancyPct),
    avgTruckOccupancyPct,
    fleetWorkedMinutes,
    fleetWorkedLabel: fleetWorkedMinutes !== null ? formatDurationLabel(fleetWorkedMinutes) : "n/d",
    fleetMaxWorkMinutes,
    fleetMaxWorkLabel: fleetMaxWorkMinutes !== null ? formatDurationLabel(fleetMaxWorkMinutes) : "n/d",
    fleetTimeUtilizationPct,
    fleetTimeUtilizationLabel: fleetTimeUtilizationPct !== null ? formatPercentLabel(fleetTimeUtilizationPct) : "n/d",
    fleetUnproductiveMinutes,
    fleetUnproductiveLabel: fleetUnproductivePct !== null ? formatPercentLabel(fleetUnproductivePct) : "n/d",
    fleetUnproductivePct,
    deliveryVisitCount,
    avgDeliveryVisitCountPerActiveTruck,
    avgDeliveryVisitCountPerActiveTruckLabel: avgDeliveryVisitCountPerActiveTruck !== null
      ? formatCountLabel(avgDeliveryVisitCountPerActiveTruck)
      : "n/d",
    lowLoadDistanceKm,
    lowLoadDistanceLabel: lowLoadDistanceKm !== null ? formatDistanceLabel(lowLoadDistanceKm) : "n/d",
    classifiedDistanceKm,
    classifiedDistanceLabel: classifiedDistanceKm !== null ? formatDistanceLabel(classifiedDistanceKm) : "n/d",
    lowLoadDistancePct,
    lowLoadDistancePctLabel: lowLoadDistancePct !== null ? formatPercentLabel(lowLoadDistancePct) : "n/d",
    loadedDistancePctLabel: rawSummary.loadedDistancePct !== null ? formatPercentLabel(rawSummary.loadedDistancePct) : "n/d",
    lowLoadThresholdPct,
    lowLoadValidDayCount,
    totalActiveTruckCount,
  };
}

function CompactMetricTile({ label, note = "", value, hoverCard = null }) {
  return <DashboardCalculationHover label={label} value={value} hoverCard={hoverCard}><div className="dashboard-stat" data-dashboard-metric={label}><dl>
    <dt>{label}<DashboardCalculationHint /></dt>
    <dd>{value ?? "n/d"}</dd>
    </dl>{note && <p className="dashboard-stat-note">{note}</p>}
  </div></DashboardCalculationHover>;
}

function MetricMeterRow({ label, note = "", ratio = null, value, hoverCard = null }) {
  const normalizedRatio = chartRatio(ratio, 1);
  return <DashboardCalculationHover label={label} value={value} hoverCard={hoverCard}><div className="dashboard-meter" data-dashboard-meter={label}>
    <div className="dashboard-meter-heading">
      <span className="dashboard-meter-label">{label}<DashboardCalculationHint /></span>
      <span className="dashboard-meter-value">{value ?? "n/d"}</span>
    </div>
    {normalizedRatio !== null && <div className="dashboard-meter-track" aria-hidden="true"><div className="dashboard-meter-fill" style={{ width: `${normalizedRatio * 100}%` }} /></div>}
    {note && <p className="dashboard-meter-note">{note}</p>}
  </div></DashboardCalculationHover>;
}

function DashboardPanel({ aside = null, children, className = "", eyebrow = "", period = null, subtitle = "", title }) {
  return <section className={joinClasses(["dashboard-card rounded-[4px]", className])} aria-label={title}>
    <header className="dashboard-card-header">
      <div><h2>{title}</h2>{(period?.label || eyebrow) && <p>{period?.label || eyebrow}</p>}{subtitle && <p>{subtitle}</p>}</div>
      <div className="dashboard-card-actions"><DashboardReportActions title={title} /></div>
    </header>
    <div className="dashboard-card-body">{aside && <div className="dashboard-kpis dashboard-kpis-single">{aside}</div>}{children}</div>
  </section>;
}

function PeriodTabs({ label = "Période", options = [], value, onChange }) {
  const select = (event, index) => {
    const next = (index + options.length) % options.length;
    onChange(options[next].key);
    event.currentTarget.parentElement?.children[next]?.focus();
  };
  return <div role="tablist" aria-label={label} className="flex gap-[2px] rounded-[4px] border border-slate-200 bg-slate-50 p-[3px] max-[480px]:grid max-[480px]:auto-cols-fr max-[480px]:grid-flow-col">
    {options.map((option, index) => {
      const selected = option.key === value;
      return <button key={option.key} type="button" role="tab" aria-selected={selected} tabIndex={selected ? 0 : -1}
        onClick={() => onChange(option.key)}
        onKeyDown={(event) => { if (event.key === "ArrowRight" || event.key === "ArrowLeft") { event.preventDefault(); select(event, index + (event.key === "ArrowRight" ? 1 : -1)); } }}
        className={joinClasses(["whitespace-nowrap rounded-[4px] px-3 py-1.5 text-[12px] focus-visible:outline-2 focus-visible:outline-accent",
          selected ? "bg-accent font-bold text-white" : "bg-transparent font-semibold text-slate-700 hover:bg-slate-200"])}>{option.label}</button>;
    })}
  </div>;
}

// « Vue d’ensemble » fits a 1080p screen without scrolling. A wide page shows the four detail lists in a right
// column; a narrower one gives them their own « Détail » view.
const VIEW_OPTIONS = [{ key: "overview", label: "Vue d’ensemble" }, { key: "detail", label: "Détail" }];
const PAGE_REPORT_KEYS = ["utilization", "peak_load", "tonnage", "compartments", "client_visits", "truck_average", "dispatch_structure"];

function OptimizationStatusLine({ summary }) {
  return <span className="inline-flex flex-wrap items-center gap-1.5 text-[12px] text-slate-700">
    <span aria-hidden="true" className="dashboard-status-square" data-tone={summary.tone} />
    {summary.text}
    {summary.stateLabel ? <strong className="font-semibold text-[var(--color-status-warning-ink)]">· {summary.stateLabel}</strong> : null}
  </span>;
}

function MiniInsightCard({ badge = null, ...props }) {
  return <DashboardPanel {...props} aside={badge} />;
}

// Page options, off for the reports: plot height (taller when the cards share a row), dashed average, the chosen
// period dark among its context days, a percent axis starting below the lowest value, and white points.
function DailyMetricChart({ days, kind = "bar", valueAccessor, valueLabel, unit, hoverRows, calculationLabel,
  plotHeight = 146, widePlotHeight = null, averageLine = null, contextDates = null, adaptivePercentAxis = false, hollowPoints = false }) {
  const canvasRef = React.useRef(null);
  const [width, setWidth] = React.useState(600);
  React.useEffect(() => {
    const observer = new ResizeObserver(([entry]) => { if (entry.contentRect.width > 0) setWidth(entry.contentRect.width); });
    observer.observe(canvasRef.current);
    return () => observer.disconnect();
  }, []);
  const wide = widePlotHeight && typeof window !== "undefined" && window.matchMedia?.("(min-width: 1280px)").matches;
  const left = 46, right = width - 8, top = 12, bottom = top + (wide ? widePlotHeight : plotHeight), height = bottom + 30;
  const values = days.map((day) => day.hasDispatch ? valueAccessor(day) : null);
  const validValues = values.filter((value) => value != null && Number.isFinite(value));
  const step = unit === "%" ? 25 : resolveRoundedTonnageAxisStep(Math.max(...validValues, 1));
  const ticks = adaptivePercentAxis && unit === "%" ? adaptivePercentTicks(validValues) : [0, 1, 2, 3, 4].map((index) => index * step);
  const minimum = ticks[0], maximum = ticks.at(-1);
  const toY = (value) => {
    const ratio = value == null ? null : chartRatio(value - minimum, maximum - minimum);
    return ratio == null ? null : bottom - ratio * (bottom - top);
  };
  const slot = (right - left) / Math.max(days.length, 1);
  const labels = chartLabelIndexes(days.length, right - left);
  const inContext = (day) => (contextDates && !contextDates.has(day.dispatchDate) ? "true" : undefined);
  const points = values.map((value, index) => ({ x: left + slot * (index + .5), y: toY(value) }));
  const linePath = (shown) => shown.map((point, index) => point.y == null ? "" : `${index === 0 || shown[index - 1].y == null ? "M" : "L"} ${point.x} ${point.y}`).join(" ");
  const path = linePath(points);
  const averageY = averageLine == null ? null : toY(averageLine);
  return <div className="dashboard-chart-scroll" data-dashboard-chart-frame="true">
    {!validValues.length && <p className="dashboard-card-note" role="status">Aucune donnée exploitable pour cette période.</p>}
    <div ref={canvasRef} className="dashboard-chart-canvas" style={{ "--chart-touch-width": `${Math.max(440, days.length * 28 + 54)}px` }}>
      <svg aria-hidden="true" viewBox={`0 0 ${width} ${height}`}>
        {ticks.map((tick) => {
          const y = toY(tick);
          return <g key={tick}><line x1={left} x2={right} y1={y} y2={y} className="dashboard-chart-grid" />
            <text x={left - 7} y={y + 4} textAnchor="end" className="dashboard-chart-label">{COMPACT_NUMBER_FORMATTER.format(tick)} {unit}</text></g>;
        })}
        {kind === "line" && (contextDates ? <>
          <path d={path} className="dashboard-chart-line" data-context="true" />
          <path d={linePath(points.map((point, index) => (inContext(days[index]) ? { ...point, y: null } : point)))} className="dashboard-chart-line" />
        </> : <path d={path} className="dashboard-chart-line" />)}
        {averageY != null && <line x1={left} x2={right} y1={averageY} y2={averageY} className="dashboard-chart-average" data-dashboard-chart-average={averageLine} />}
        {days.map((day, index) => {
          const point = points[index];
          const barWidth = Math.min(24, slot * .62);
          return <g key={day.dispatchDate || index} data-context={inContext(day)}>
            {point.y == null ? <text x={point.x} y={bottom - 4} textAnchor="middle" className="dashboard-chart-label">—</text>
              : kind === "bar" ? <rect data-tonnage-bar={day.dispatchDate} x={point.x - barWidth / 2} y={point.y} width={barWidth} height={bottom - point.y} rx="2" className="dashboard-chart-bar" />
                : <circle cx={point.x} cy={point.y} r={hollowPoints ? 3.5 : 3} className={hollowPoints ? "dashboard-chart-point" : "dashboard-chart-bar"} />}
            {labels.has(index) && <text x={point.x} y={bottom + 22} textAnchor={index === 0 ? "start" : index === days.length - 1 ? "end" : "middle"} className="dashboard-chart-label dashboard-chart-date">{day.dateLabel || day.dayLabel}</text>}
          </g>;
        })}
      </svg>
      {days.map((day, index) => <HoverCard key={day.dispatchDate || index} title={formatAnalyticsHoverDateLabel(day)}
        content={<DashboardHoverCardContent note={[DASHBOARD_CALCULATIONS[calculationLabel], resolveDayAnalyticsNote(day)].filter(Boolean).join(" ")} rows={hoverRows(day)} />}
        flat openOnClick referenceAsChild placement="top" maxWidth={300} delay={80} accentColor={LINE_PRIMARY_COLOR}>
        <button type="button" className="dashboard-chart-target" data-dashboard-chart-day={day.dispatchDate}
          aria-label={`${formatAnalyticsHoverDateLabel(day)} : ${Number.isFinite(values[index]) ? valueLabel(values[index]) : "n/d"}. Détails.`}
          style={{ left: `${(left + index * slot) / width * 100}%`, width: `${slot / width * 100}%`, top: `${top / height * 100}%`, height: `${(bottom - top) / height * 100}%` }} />
      </HoverCard>)}
    </div>
  </div>;
}

const peakLoadHoverRows = (day) => [
  { label: "Remplissage maximal moyen", value: day.avgPeakLoadFillRateLabel },
  { label: "Temps assigné moyen", value: formatDurationLabel(day.avgAssignedDurationMinutes) },
  { label: "Occupation moyenne", value: formatPercentLabel(day.avgTimeBudgetConsumedPct) },
  { label: "Camions utilisés parmi les actifs", value: formatPercentLabel(day.usedFleetPct) },
];
const tonnageHoverRows = (day) => [
  { label: "Tonnage planifié", value: day.hasDispatch ? formatTonnageLabel(day.plannedTonnageTons) : "Hors calcul" },
  { label: "Commandes dispatch", value: day.hasDispatch ? formatOrdersCountLabel(day.dispatchOrderCount) : "n/d" },
  { label: "Compartiments utilisés", value: day.hasDispatch ? formatCountLabel(day.usedCompartments) : "n/d" },
];

// Page trends: every loaded dispatch, the chosen period dark, its average dashed.
const TRENDS = {
  tonnage: { title: "Tonnage planifié", kind: "bar", unit: "t", calculationLabel: "Tonnage du dispatch", hoverRows: tonnageHoverRows,
    value: (day) => day.plannedTonnageTons, valueLabel: formatTonnageLabel,
    average: (summary) => summary.avgPlannedTonnageTons, averageLabel: (summary) => summary.avgPlannedTonnageLabel },
  peak_load: { title: "Remplissage maximal des camions", kind: "line", unit: "%", calculationLabel: "Remplissage maximal moyen", hoverRows: peakLoadHoverRows,
    value: (day) => day.avgPeakLoadFillRatePct, valueLabel: (value) => value === null ? "n/d" : formatPercentLabel(value),
    average: (summary) => summary.avgPeakLoadFillRatePct, averageLabel: (summary) => summary.avgPeakLoadFillRateLabel },
};

function TrendChartPanel({ indicatorKey, period, summary, contextDays }) {
  const trend = TRENDS[indicatorKey];
  return <DashboardIndicatorContext.Provider value={indicatorKey}>
    <DashboardPanel title={trend.title} period={period} subtitle={`Par dispatch · moyenne ${trend.averageLabel(summary)}`} className="dashboard-trend-card">
      <DailyMetricChart days={contextDays} kind={trend.kind} unit={trend.unit} calculationLabel={trend.calculationLabel} hoverRows={trend.hoverRows}
        valueAccessor={trend.value} valueLabel={trend.valueLabel} plotHeight={220} widePlotHeight={270} averageLine={trend.average(summary)}
        contextDates={new Set(period.days.map((day) => day.dispatchDate))} adaptivePercentAxis hollowPoints />
    </DashboardPanel>
  </DashboardIndicatorContext.Provider>;
}

function MiniLoadPerformanceCard({ current, period = EMPTY_PERIOD }) {
  const days = period.days.filter((day) => day.hasDispatch);
  const summary = buildPeriodAnalyticsSummary(period, resolveCurrentPeriodDay(period) || current);
  return <MiniInsightCard title="Remplissage maximal moyen des camions utilisés" className="fleet-peak-card" period={period}>
    <div className="dashboard-kpis dashboard-kpis-single"><CompactMetricTile label={period.key === "day" ? "Moyenne des camions utilisés" : "Moyenne par dispatch"} value={summary.avgPeakLoadFillRateLabel} /></div>
    <DailyMetricChart days={days} kind="line" unit="%" calculationLabel="Remplissage maximal moyen" valueAccessor={(day) => day.avgPeakLoadFillRatePct} valueLabel={(value) => value === null ? "n/d" : formatPercentLabel(value)}
      hoverRows={peakLoadHoverRows} />
    <p className="dashboard-card-note">Pour chaque dispatch, moyenne du remplissage maximal des camions utilisés, puis moyenne des dispatchs.<span className="dashboard-chart-mobile-note"> Faites défiler le graphique.</span></p>
  </MiniInsightCard>;
}

function TrendCard({ current, period, summary, historyPeriod = EMPTY_PERIOD }) {
  const resolvePeakDay = (days = [], accessor) => {
    const resolvedDays = Array.isArray(days) ? days.filter(Boolean) : [];
    let bestDay = null;
    let bestValue = -1;

    for (const day of resolvedDays) {
      const candidateValue = Math.max(toChartNumber(accessor(day), -1), -1);
      if (candidateValue > bestValue) {
        bestValue = candidateValue;
        bestDay = day;
      }
    }

    return bestValue > 0 ? bestDay : null;
  };

  const resolveHighlightZone = (primarySeries, secondarySeries, index, chartLeftValue, chartRightValue) => {
    const point = primarySeries[index] || secondarySeries[index];

    if (!point) {
      return null;
    }

    const previousPoint = primarySeries[index - 1] || secondarySeries[index - 1] || point;
    const nextPoint = primarySeries[index + 1] || secondarySeries[index + 1] || point;
    const left = index === 0 ? chartLeftValue : (previousPoint.x + point.x) / 2;
    const right = index === primarySeries.length - 1 ? chartRightValue : (point.x + nextPoint.x) / 2;

    return {
      left,
      width: Math.max(right - left, 8),
    };
  };

  const formatGapLabel = (value) => {
    if (!Number.isFinite(value)) {
      return "n/d";
    }

    const roundedValue = Math.round(value * 10) / 10;
    const absoluteValue = Math.abs(roundedValue);
    return `${roundedValue > 0 ? "+" : roundedValue < 0 ? "-" : ""}${COMPACT_NUMBER_FORMATTER.format(absoluteValue)} pts`;
  };

  const comparisonPeriod = period.key !== "day" && Array.isArray(historyPeriod?.days) && historyPeriod.days.length ? historyPeriod : EMPTY_PERIOD;
  const comparisonSummary = comparisonPeriod.days.length ? buildPeriodAnalyticsSummary(comparisonPeriod, current) : null;

  if (period.key === "day") {
    const currentDay = resolveCurrentPeriodDay(period);
    const occupationRatio = clamp((summary.avgTimeBudgetConsumedPct || 0) / 100, 0, 1);
    const fleetRatio = clamp((summary.usedFleetPct || 0) / 100, 0, 1);
    const contextDays = comparisonPeriod.days.length ? comparisonPeriod.days : [];
    const contextHistoryPresentation = comparisonPeriod.days.length ? resolveHistoryPresentation(comparisonPeriod) : null;
    const contextLabelIndexes = resolveVisibleLabelIndexes(contextDays.length, 5);
    const contextChartLeft = 26;
    const contextChartRight = 494;
    const contextChartTop = 10;
    const contextChartHeight = 72;
    const contextChartConfig = {
      bottomPadding: 20,
      height: 102,
      topPadding: 10,
      width: 468,
      xStart: 26,
    };
    const contextPrimaryPoints = buildChartPoints(contextDays, (day) => day.avgTimeBudgetConsumedPct, { ...contextChartConfig, maxValue: 100 });
    const contextSecondaryPoints = buildChartPoints(contextDays, (day) => day.usedFleetPct, { ...contextChartConfig, maxValue: 100 });
    const contextAreaPath = buildAreaPath(contextPrimaryPoints, 82);
    const contextPrimaryPath = buildLinePath(contextPrimaryPoints);
    const contextSecondaryPath = buildLinePath(contextSecondaryPoints);
    const contextPeakDay = resolvePeakDay(contextDays, (day) => day?.avgTimeBudgetConsumedPct);
    const contextPeakIndex = contextPeakDay ? contextDays.findIndex((day) => day?.dispatchDate === contextPeakDay.dispatchDate) : -1;
    const contextHighlightZone = contextPeakIndex >= 0
      ? resolveHighlightZone(contextPrimaryPoints, contextSecondaryPoints, contextPeakIndex, contextChartLeft, contextChartRight)
      : null;

    return (
      <DashboardPanel
        className="dashboard-occupation-card"
        eyebrow={period.reportDateLabel ? period.label : "Hier"}
        title="Occupation et flotte"
        subtitle={`Lecture instantanée du dispatch ${period.reportDateLabel || "d’hier"} entre occupation moyenne des heures max et part de flotte réellement mobilisée.`}
        tone="slate"
        aside={
          <div className="text-right max-[480px]:text-left">
            <p className="break-words text-[1.65rem] font-black leading-none tracking-[-0.05em] text-[rgb(24,35,31)] max-[480px]:text-[1.35rem] max-[480px]:tracking-normal">{summary.avgTimeBudgetConsumedLabel}</p>
          </div>
        }
      >
        <div className="grid min-w-0 gap-3 rounded-[4px] border border-[rgba(115,134,145,0.15)] bg-white/76 p-3  max-[480px]:rounded-[4px] max-[480px]:p-2  lg:grid-cols-[12rem_minmax(0,1fr)]">
          <div className="min-w-0 rounded-[4px] border border-[rgba(115,134,145,0.15)] bg-white px-3 py-3.5  max-[480px]:rounded-[4px] max-[480px]:px-2 max-[480px]:py-2.5">
            <div
              className="mx-auto grid h-28 w-28 place-items-center rounded-full p-2.5 "
              style={{ background: `conic-gradient(rgba(94,118,132,0.94) 0deg ${(occupationRatio * 360).toFixed(1)}deg, rgba(198,210,218,0.28) ${(occupationRatio * 360).toFixed(1)}deg 360deg)` }}
            >
              <div className="grid h-full w-full place-items-center rounded-full bg-[rgba(245,249,251,0.96)] text-center">
                <div>
                  <p className="text-[0.58rem] font-black uppercase tracking-[0.16em] text-[rgba(75,95,106,0.74)]">Occupation</p>
                  <p className="mt-1 text-[1.5rem] font-black leading-none tracking-[-0.05em] text-[rgb(35,47,43)]">{summary.avgTimeBudgetConsumedLabel}</p>
                </div>
              </div>
            </div>
            <p className="mt-3 text-center text-[0.72rem] leading-[1.35] text-[rgba(78,84,74,0.8)]">
              {currentDay?.avgAssignedDurationLabel || "n/d"} assignées en moyenne par camion actif.
            </p>
          </div>

          <div className="grid min-w-0 gap-2.5">
            <div className="grid gap-2 sm:grid-cols-[minmax(0,1.02fr)_minmax(0,0.98fr)]">
              <MetricMeterRow
                label="Occupation moyenne"
                note="Part des heures max consommées"
                ratio={occupationRatio}
                tone="slate"
                value={summary.avgTimeBudgetConsumedLabel}
              />
              <MetricMeterRow
                label="Flotte mobilisée"
                note={`${formatCountLabel(summary.usedTruckCount)} / ${formatCountLabel(summary.activeTruckCount)} camions`}
                ratio={fleetRatio}
                tone="slate"
                value={summary.usedFleetLabel}
              />
            </div>

            <div className="grid gap-2 sm:grid-cols-3">
              <CompactMetricTile label="Temps assigné" tone="slate" value={summary.avgAssignedDurationLabel} />
              <CompactMetricTile label="Camions actifs" tone="slate" value={formatCountLabel(summary.activeTruckCount)} />
              <CompactMetricTile label="Camions mobilisés" tone="slate" value={formatCountLabel(summary.usedTruckCount)} />
            </div>
          </div>
        </div>

        {contextDays.length > 1 ? (
          <div className="mt-3 min-w-0 rounded-[4px] border border-[rgba(115,134,145,0.15)] bg-white p-3  max-[480px]:rounded-[4px] max-[480px]:p-2 ">
            <div className="flex min-w-0 flex-wrap items-center justify-between gap-2 pb-2 max-[480px]:items-start">
              <div className="min-w-0">
                <p className="break-words text-[0.6rem] font-black uppercase leading-[1.15] tracking-[0.16em] text-[rgba(75,95,106,0.66)] max-[480px]:text-[0.54rem] max-[480px]:tracking-[0.1em]">Contexte 30 jours</p>
                <p className="mt-0.5 break-words text-[0.82rem] font-semibold leading-[1.25] text-[rgb(52,66,60)] max-[480px]:text-[0.74rem]">Occupation vs flotte sur le mois visible</p>
              </div>
              {contextHistoryPresentation ? (
                <span className={joinClasses(["inline-flex max-w-full items-center rounded-[4px] px-2.5 py-1 text-[0.72rem] font-semibold max-[480px]:px-2 max-[480px]:text-[0.66rem]", contextHistoryPresentation.className])}>
                  {contextHistoryPresentation.label}
                </span>
              ) : null}
            </div>

            <div className="min-w-0 overflow-hidden rounded-[4px] border border-[rgba(115,134,145,0.15)] bg-white/80 p-2  max-[480px]:rounded-[4px] max-[480px]:p-1 " data-dashboard-chart-frame="true">
              <svg aria-hidden="true" className="block h-auto w-full max-[480px]:min-h-[6.5rem]" viewBox="0 0 520 96">
                {[0, 50, 100].map((tick) => {
                  const y = contextChartTop + (((100 - tick) / 100) * contextChartHeight);
                  return (
                    <g key={tick}>
                      <line x1={contextChartLeft} y1={y} x2={contextChartRight} y2={y} stroke={GRID_COLOR} strokeWidth="1" />
                      <text x="0" y={y + 4} fill="rgba(71,85,80,0.68)" fontSize="10" fontWeight="700">{tick}%</text>
                    </g>
                  );
                })}
                {contextHighlightZone ? (
                  <rect x={contextHighlightZone.left} y={contextChartTop} width={contextHighlightZone.width} height={contextChartHeight} rx="4" fill="rgba(196,150,85,0.08)" />
                ) : null}
                {contextAreaPath ? <path d={contextAreaPath} fill="rgba(95,127,114,0.1)" /> : null}
                {contextSecondaryPath ? <path d={contextSecondaryPath} fill="none" stroke={LINE_SECONDARY_COLOR} strokeWidth="2.2" strokeDasharray="5 5" strokeLinecap="round" strokeLinejoin="round" /> : null}
                {contextPrimaryPath ? <path d={contextPrimaryPath} fill="none" stroke={LINE_PRIMARY_COLOR} strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" /> : null}
                {contextPrimaryPoints.map((point, index) => (
                  <circle key={`context-primary-${point.item.dispatchDate || index}`} cx={point.x} cy={point.y} r="2.4" fill={LINE_PRIMARY_COLOR} />
                ))}
                {contextSecondaryPoints.map((point, index) => (
                  <circle key={`context-secondary-${point.item.dispatchDate || index}`} cx={point.x} cy={point.y} r="2.2" fill="#f7efe0" stroke={LINE_SECONDARY_COLOR} strokeWidth="1.5" />
                ))}
                {contextPeakIndex >= 0 && contextPrimaryPoints[contextPeakIndex] ? (
                  <circle cx={contextPrimaryPoints[contextPeakIndex].x} cy={contextPrimaryPoints[contextPeakIndex].y} r="5.8" fill="rgba(196,150,85,0.14)" />
                ) : null}
                {contextPeakIndex >= 0 && contextPrimaryPoints[contextPeakIndex] ? (
                  <circle cx={contextPrimaryPoints[contextPeakIndex].x} cy={contextPrimaryPoints[contextPeakIndex].y} r="3.2" fill={LINE_PRIMARY_COLOR} stroke="#fff" strokeWidth="1.8" />
                ) : null}
                {contextDays.map((day, index) => {
                  const labelPoint = contextPrimaryPoints[index];

                  if (!contextLabelIndexes.has(index) || !labelPoint) {
                    return null;
                  }

                  return (
                    <text
                      key={`context-label-${day.dispatchDate || index}`}
                      x={labelPoint.x}
                      y="94"
                      fill="rgba(60,72,67,0.82)"
                      fontSize="10"
                      fontWeight="800"
                      textAnchor="middle"
                    >
                      {resolveAxisLabel(day, comparisonPeriod.key) || `J${index + 1}`}
                    </text>
                  );
                })}
              </svg>
            </div>

            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[0.72rem] text-[rgba(61,74,69,0.78)]">
              <p>
                {contextPeakDay
                  ? `Pic d’occupation le ${contextPeakDay.dateLabel || contextPeakDay.dayLabel || "dernier point"} à ${formatPercentLabel(contextPeakDay.avgTimeBudgetConsumedPct)}.`
                  : "Aucun pic notable disponible sur l’historique visible."}
              </p>
              <p>{comparisonSummary ? resolvePeriodCoverageLabel(comparisonPeriod, comparisonSummary) : ""}</p>
            </div>
          </div>
        ) : null}

        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[0.74rem] text-[rgba(61,74,69,0.78)]">
          <p>{summary.usedFleetAverageNote}</p>
          <p>{resolvePeriodCoverageLabel(period, summary)}</p>
        </div>
      </DashboardPanel>
    );
  }

  const historyPresentation = resolveHistoryPresentation(period);
  const lineDays = period.days.length ? period.days : [current];
  const dispatchedDays = lineDays.filter((day) => day?.hasDispatch);
  const labelIndexes = resolveVisibleLabelIndexes(lineDays.length, period.key === "month" ? 6 : 7);
  const chartLeft = 34;
  const chartRight = 534;
  const chartTop = 6;
  const chartHeight = 74;
  const chartConfig = {
    bottomPadding: 12,
    height: 94,
    topPadding: 6,
    width: 478,
    xStart: 34,
  };
  const primaryPoints = buildChartPoints(lineDays, (day) => day.truckOccupancyPct, { ...chartConfig, maxValue: 100 });
  const secondaryPoints = buildChartPoints(lineDays, (day) => day.fleetUsagePct, { ...chartConfig, maxValue: 100 });
  const primaryPath = buildLinePath(primaryPoints);
  const secondaryPath = buildLinePath(secondaryPoints);
  const areaPath = buildAreaPath(primaryPoints, chartTop + chartHeight);
  const endPrimaryPoint = primaryPoints.at(-1);
  const hoverZones = lineDays.map((day, index) => {
    const point = primaryPoints[index] || secondaryPoints[index];

    if (!point) {
      return null;
    }

    const previousPoint = primaryPoints[index - 1] || secondaryPoints[index - 1] || point;
    const nextPoint = primaryPoints[index + 1] || secondaryPoints[index + 1] || point;
    const left = index === 0 ? chartLeft : (previousPoint.x + point.x) / 2;
    const right = index === lineDays.length - 1 ? chartRight : (point.x + nextPoint.x) / 2;

    return {
      day,
      left,
      width: Math.max(right - left, 8),
    };
  }).filter(Boolean);
  const peakOccupationDay = resolvePeakDay(dispatchedDays, (day) => day?.truckOccupancyPct);
  const peakOccupationIndex = peakOccupationDay ? lineDays.findIndex((day) => day?.dispatchDate === peakOccupationDay.dispatchDate) : -1;
  const peakHighlightZone = peakOccupationIndex >= 0
    ? resolveHighlightZone(primaryPoints, secondaryPoints, peakOccupationIndex, chartLeft, chartRight)
    : null;
  const averageGapValue = dispatchedDays.length
    ? dispatchedDays.reduce(
      (sum, day) => sum + Math.abs(toChartNumber(day?.fleetUsagePct, 0) - toChartNumber(day?.truckOccupancyPct, 0)),
      0,
    ) / dispatchedDays.length
    : null;
  const largestGapSample = dispatchedDays.reduce((bestSample, day) => {
    const gapValue = Math.abs(toChartNumber(day?.fleetUsagePct, 0) - toChartNumber(day?.truckOccupancyPct, 0));
    if (!bestSample || gapValue > bestSample.gapValue) {
      return {
        day,
        gapValue,
      };
    }
    return bestSample;
  }, null);

  return (
    <DashboardPanel
        className="dashboard-occupation-card"
      title="Occupation et utilisation des camions"
      subtitle={`${period.label} · camions avec commandes vs flotte active.`}
      subtitleClassName="xl:max-w-none xl:whitespace-nowrap xl:text-[0.62rem]"
      tone="slate"
      aside={
        <div className="text-right max-[480px]:text-left">
          <p className="break-words text-[1.62rem] font-black leading-none tracking-[-0.05em] text-[rgb(24,35,31)] max-[480px]:text-[1.35rem] max-[480px]:tracking-normal">{summary.avgFleetUsageLabel}</p>
        </div>
      }
    >
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-2 pb-0.8 max-[480px]:items-start">
        <div className="flex min-w-0 flex-wrap items-center gap-3 text-[0.8rem] font-semibold text-[rgba(48,62,55,0.84)] max-[480px]:gap-2 max-[480px]:text-[0.7rem]">
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <span className="h-2 w-6 rounded-full" style={{ background: LINE_PRIMARY_COLOR }} />
            Occupation moyenne
          </span>
          <span className="inline-flex min-w-0 items-center gap-1.5 text-[rgba(48,62,55,0.72)]">
            <span className="h-2 w-6 rounded-full border border-[rgba(196,150,85,0.4)] bg-[rgba(231,207,164,0.3)]" />
            Flotte utilisée
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={joinClasses(["inline-flex max-w-full items-center rounded-[4px] px-2.5 py-1 text-[0.78rem] font-semibold max-[480px]:px-2 max-[480px]:text-[0.66rem]", historyPresentation.className])}>
            {historyPresentation.label}
          </span>
        </div>
      </div>

      <div className="grid gap-1 pb-0.8 sm:grid-cols-2">
        <CompactMetricTile
          label="Pic d’occupation"
          tone="slate"
          value={peakOccupationDay ? formatPercentLabel(peakOccupationDay.truckOccupancyPct) : "n/d"}
          note={peakOccupationDay ? (peakOccupationDay.dateLabel || peakOccupationDay.dayLabel || "jour de pic") : "Aucun pic détecté"}
        />
        <CompactMetricTile
          label="Écart flotte"
          tone="slate"
          value={formatGapLabel(largestGapSample?.gapValue ?? averageGapValue)}
          note={largestGapSample?.day ? `plus fort écart le ${largestGapSample.day.dateLabel || largestGapSample.day.dayLabel}` : "écart moyen occupation / flotte"}
        />
      </div>

      <div className="min-w-0 overflow-hidden rounded-[4px] border border-[rgba(115,134,145,0.15)] bg-white/74 p-1  max-[480px]:rounded-[4px] " data-dashboard-chart-frame="true">
        <svg aria-hidden="true" className="block h-auto w-full max-[480px]:min-h-[6.75rem]" viewBox="0 0 560 108">
          {[
            { from: 0, to: 50, fill: "rgba(116,161,134,0.06)" },
            { from: 50, to: 75, fill: "rgba(224,190,113,0.08)" },
            { from: 75, to: 100, fill: "rgba(211,133,101,0.08)" },
          ].map((band) => {
            const y = chartTop + (((100 - band.to) / 100) * chartHeight);
            const height = ((band.to - band.from) / 100) * chartHeight;
            return <rect key={`${band.from}-${band.to}`} x={chartLeft} y={y} width={chartRight - chartLeft} height={height} fill={band.fill} rx="4" />;
          })}
          {peakHighlightZone ? (
            <rect x={peakHighlightZone.left} y={chartTop} width={peakHighlightZone.width} height={chartHeight} rx="4" fill="rgba(196,150,85,0.08)" />
          ) : null}
          {[0, 25, 50, 75, 100].map((tick) => {
            const y = chartTop + (((100 - tick) / 100) * chartHeight);
            return (
              <g key={tick}>
                <line x1={chartLeft} y1={y} x2={chartRight} y2={y} stroke={GRID_COLOR} strokeWidth="1" />
                <text x="0" y={y + 4} fill="rgba(71,85,80,0.72)" fontSize="11" fontWeight="700">{tick}%</text>
              </g>
            );
          })}
          {areaPath ? <path d={areaPath} fill="rgba(95,127,114,0.12)" /> : null}
          {secondaryPath ? <path d={secondaryPath} fill="none" stroke={LINE_SECONDARY_COLOR} strokeWidth="2.5" strokeDasharray="6 6" strokeLinecap="round" strokeLinejoin="round" /> : null}
          {primaryPath ? <path d={primaryPath} fill="none" stroke={LINE_PRIMARY_COLOR} strokeWidth="3.25" strokeLinecap="round" strokeLinejoin="round" /> : null}
          {primaryPoints.map((point, index) => (
            <circle key={`primary-${point.item.dispatchDate || index}`} cx={point.x} cy={point.y} r="2.8" fill={LINE_PRIMARY_COLOR} />
          ))}
          {secondaryPoints.map((point, index) => (
            <circle key={`secondary-${point.item.dispatchDate || index}`} cx={point.x} cy={point.y} r="2.6" fill="#f7efe0" stroke={LINE_SECONDARY_COLOR} strokeWidth="1.7" />
          ))}
          {peakOccupationIndex >= 0 && primaryPoints[peakOccupationIndex] ? (
            <circle cx={primaryPoints[peakOccupationIndex].x} cy={primaryPoints[peakOccupationIndex].y} r="7.4" fill="rgba(196,150,85,0.16)" />
          ) : null}
          {peakOccupationIndex >= 0 && primaryPoints[peakOccupationIndex] ? (
            <circle cx={primaryPoints[peakOccupationIndex].x} cy={primaryPoints[peakOccupationIndex].y} r="4" fill={LINE_PRIMARY_COLOR} stroke="#fff" strokeWidth="2" />
          ) : null}
          {peakOccupationDay && peakOccupationIndex >= 0 && primaryPoints[peakOccupationIndex] ? (
            <g>
              <rect
                x={clamp(primaryPoints[peakOccupationIndex].x - 48, chartLeft + 2, chartRight - 98)}
                y={Math.max(primaryPoints[peakOccupationIndex].y - 34, 4)}
                rx="4"
                width="96"
                height="28"
                fill="rgba(255,252,246,0.96)"
                stroke="rgba(196,150,85,0.34)"
              />
              <text
                x={clamp(primaryPoints[peakOccupationIndex].x, chartLeft + 50, chartRight - 50)}
                y={Math.max(primaryPoints[peakOccupationIndex].y - 22, 16)}
                fill="rgba(110,79,30,0.84)"
                fontSize="10"
                fontWeight="800"
                textAnchor="middle"
              >
                Pic {formatPercentLabel(peakOccupationDay.truckOccupancyPct)}
              </text>
              <text
                x={clamp(primaryPoints[peakOccupationIndex].x, chartLeft + 50, chartRight - 50)}
                y={Math.max(primaryPoints[peakOccupationIndex].y - 12, 27)}
                fill="rgba(82,89,78,0.76)"
                fontSize="9.5"
                fontWeight="700"
                textAnchor="middle"
              >
                {peakOccupationDay.dateLabel || peakOccupationDay.dayLabel || "jour de pic"}
              </text>
            </g>
          ) : null}
          {endPrimaryPoint && summary.hasData ? (
            <text
              x={Math.min(endPrimaryPoint.x + 10, 524)}
              y={Math.max(endPrimaryPoint.y - 8, 12)}
              fill="rgba(35,47,43,0.84)"
              fontSize="11"
              fontWeight="800"
              textAnchor={endPrimaryPoint.x > 500 ? "end" : "start"}
            >
              {summary.avgTruckOccupancyLabel}
            </text>
          ) : null}
          {lineDays.map((day, index) => {
            const labelPoint = primaryPoints[index];

            if (!labelIndexes.has(index) || !labelPoint) {
              return null;
            }

            return (
              <text
                key={`label-${day.dispatchDate || index}`}
                x={labelPoint.x}
                y="102"
                fill="rgba(60,72,67,0.82)"
                fontSize="11"
                fontWeight="800"
                textAnchor="middle"
              >
                {resolveAxisLabel(day, period.key) || `J${index + 1}`}
              </text>
            );
          })}
          {hoverZones.map((zone) => (
            <HoverCard flat
              key={`trend-zone-${zone.day.dispatchDate || zone.left}`}
              title={formatAnalyticsHoverDateLabel(zone.day)}
              content={(
                <DashboardHoverCardContent
                  eyebrow={zone.day.hasDispatch ? "Journée incluse dans les indicateurs" : "Journée hors calcul"}
                  note={resolveDayAnalyticsNote(zone.day)}
                  rows={[
                    { label: "Occupation moyenne", value: zone.day.hasDispatch ? formatPercentLabel(zone.day.truckOccupancyPct) : "Hors calcul" },
                    { label: "Flotte utilisée", value: zone.day.hasDispatch ? formatPercentLabel(zone.day.fleetUsagePct) : "Hors calcul" },
                    { label: "Temps assigné moyen", value: zone.day.hasDispatch ? formatDurationLabel(zone.day.avgAssignedDurationMinutes) : "Hors calcul" },
                    { label: "Camions actifs", value: zone.day.hasDispatch ? formatCountLabel(zone.day.activeTruckCount) : "n/d" },
                    { label: "Camions mobilisés", value: zone.day.hasDispatch ? formatCountLabel(zone.day.usedTruckCount) : "n/d" },
                  ]}
                />
              )}
              placement="top"
              delay={80}
              maxWidth={280}
              accentColor={LINE_PRIMARY_COLOR}
              referenceAsChild
            >
              <g className="cursor-help">
                <rect x={zone.left} y={chartTop} width={zone.width} height={chartHeight} fill="transparent" />
              </g>
            </HoverCard>
          ))}
        </svg>
      </div>

      <div className="mt-0 flex flex-wrap items-center justify-between gap-2 text-[0.64rem] text-[rgba(61,74,69,0.76)]">
        <p>{summary.usedFleetAverageNote}</p>
        <p>{resolvePeriodCoverageLabel(period, summary)}</p>
      </div>
    </DashboardPanel>
  );
}

function analyticsHint(period, summary, title, rows, note = resolvePeriodCoverageLabel(period, summary)) {
  return { title: `${period.label} · ${title}`, content: <DashboardHoverCardContent note={note} rows={rows} /> };
}

function MiniClientStopsCard({ className = "", period = EMPTY_PERIOD, summary }) {
  return <MiniInsightCard className={className} title="Visites clients" period={period}>
    <div className="dashboard-kpis" data-dashboard-hover-target="mini-client-stops">
      <CompactMetricTile label="Arrêts / camion" value={summary.avgBusinessStopCountLabel} note="Par camion utilisé"
        hoverCard={analyticsHint(period, summary, "Visites clients", [
          { label: "Arrêts / camion", value: summary.avgBusinessStopCountLabel },
          { label: "Camions utilisés", value: formatCountLabel(summary.usedTruckCount) },
          { label: "Camions utilisés parmi les actifs", value: summary.usedFleetLabel },
        ])} />
      <CompactMetricTile label="Tonnes / client" value={summary.avgTonsPerDeliveryStopLabel} note="Par arrêt de livraison" />
    </div>
    <div className="dashboard-meters">
      <MetricMeterRow label="Temps route chargé" ratio={chartRatio(summary.avgHighLoadDriveSharePct)} value={summary.avgHighLoadDriveShareLabel}
        hoverCard={analyticsHint(period, summary, "Temps route chargé", [
          { label: "Temps route chargé", value: summary.avgHighLoadDriveShareLabel },
          { label: "Distance / camion", value: summary.avgDistancePerUsedTruckLabel },
          { label: "Temps assigné moyen", value: summary.avgAssignedDurationLabel },
        ])} />
      <MetricMeterRow label="Camions utilisés parmi les actifs" ratio={chartRatio(summary.usedFleetPct)} value={summary.usedFleetLabel}
        hoverCard={analyticsHint(period, summary, "Camions utilisés parmi les actifs", [
          { label: "Camions utilisés", value: formatCountLabel(summary.usedTruckCount) },
          { label: "Camions actifs", value: formatCountLabel(summary.activeTruckCount) },
        ], "Sur la période, nombre cumulé de camions utilisés ÷ nombre cumulé de camions actifs. Un même camion est compté pour chaque dispatch.")} />
    </div>
    <div className="dashboard-support">
      <CompactMetricTile label="Camions utilisés" value={formatCountLabel(summary.usedTruckCount)} note={period.key === "day" ? "Sur ce dispatch" : "Moyenne par dispatch"} />
      <CompactMetricTile label="Camions actifs" value={formatCountLabel(summary.activeTruckCount)} note={period.key === "day" ? "Sur ce dispatch" : "Moyenne par dispatch"} />
    </div>
    <p className="dashboard-card-note">Camions utilisés : avec au moins un voyage ou une durée planifiée positive.</p>
  </MiniInsightCard>;
}

function TonnageCard({ current, period, summary }) {
  const currentDay = resolveCurrentPeriodDay(period);
  if (period.key === "day") return <DashboardPanel title="Tonnage du dispatch" period={period}>
    <div className="dashboard-kpis dashboard-kpis-three">
      <CompactMetricTile label="Tonnage du dispatch" value={summary.avgPlannedTonnageLabel} />
      <CompactMetricTile label="Tonnes / camion" value={summary.avgTonnagePerUsedTruckLabel} note="Camion utilisé" />
      <CompactMetricTile label="Tonnes / client" value={summary.avgTonsPerDeliveryStopLabel} note="Arrêt de livraison" />
    </div>
    <div className="dashboard-support">
      <CompactMetricTile label="Commandes" value={formatOrdersCountLabel(currentDay?.dispatchOrderCount || 0)} />
      <CompactMetricTile label="Produits" value={summary.dispatchProductCountLabel} />
    </div>
    <p className="dashboard-card-note">{summary.compartmentNote} · {resolvePeriodCoverageLabel(period, summary)}</p>
  </DashboardPanel>;
  const days = period.days.length ? period.days : [current];
  return <DashboardPanel title="Tonnage quotidien" period={period}>
    <div className="dashboard-kpis dashboard-kpis-single">
      <CompactMetricTile label="Moyenne / dispatch" value={summary.avgPlannedTonnageLabel} />
    </div>
    <DailyMetricChart days={days} unit="t" calculationLabel="Tonnage du dispatch" valueAccessor={(day) => day.plannedTonnageTons} valueLabel={formatTonnageLabel}
      hoverRows={tonnageHoverRows} />
    <p className="dashboard-card-note">{resolvePeriodCoverageLabel(period, summary)} · Tonnage planifié.<span className="dashboard-chart-mobile-note"> Faites défiler le graphique.</span></p>
  </DashboardPanel>;
}

function CompartmentsOverviewCard({ period = EMPTY_PERIOD, summary }) {
  return <DashboardPanel title="Compartiments" period={period}>
    <div className="dashboard-meters">
      <MetricMeterRow label="Compartiments utilisés" value={summary.avgCompartmentUsageLabel} ratio={chartRatio(summary.avgCompartmentUsagePct)}
        hoverCard={analyticsHint(period, summary, "Compartiments utilisés", [
          { label: "Taux utilisés", value: summary.avgCompartmentUsageLabel },
          { label: "Compartiments / camion", value: summary.avgCompartmentsUsedLabel },
          { label: "Capacité déclarée", value: summary.totalCompartmentsLabel },
        ])} />
      <MetricMeterRow label="Remplissage moyen" value={summary.avgCompartmentFillLabel} ratio={chartRatio(summary.avgCompartmentFillPct)} note="Sur les compartiments mobilisés"
        hoverCard={analyticsHint(period, summary, "Remplissage compartiments", [
          { label: "Remplissage moyen", value: summary.avgCompartmentFillLabel },
          { label: "Compartiments / camion", value: summary.avgCompartmentsUsedLabel },
          { label: "Capacité déclarée", value: summary.totalCompartmentsLabel },
        ])} />
    </div>
    <div className="dashboard-support">
      <CompactMetricTile label="Compartiments / camion" value={summary.avgCompartmentsUsedLabel} note="Utilisés en moyenne" />
      <CompactMetricTile label="Capacité déclarée" value={summary.totalCompartmentsLabel} note="Compartiments" />
    </div>
    <p className="dashboard-card-note">{resolvePeriodCoverageLabel(period, summary)}</p>
  </DashboardPanel>;
}

function TruckAverageCard({ period = EMPTY_PERIOD, summary }) {
  const lowLoadHoverContent = <DashboardHoverCardContent
    note={`${resolveLowLoadCoverageLabel(period, summary)} · Données planifiées du dispatch, pas une télémétrie.`}
    rows={[
      { label: "Part faible charge", value: summary.lowLoadDistancePctLabel },
      { label: "Km faible charge (<25 %)", value: summary.lowLoadDistanceLabel },
      { label: "Km classifiés", value: summary.classifiedDistanceLabel },
      { label: "Formule", value: "km faible charge / km classifiés × 100" },
    ]} />;
  return <DashboardPanel title="Camion moyen" period={period}>
    <div className="dashboard-kpis dashboard-kpis-three">
      <CompactMetricTile label="Tonnes / camion" value={summary.avgTonnagePerUsedTruckLabel} note="Camion utilisé"
        hoverCard={analyticsHint(period, summary, "Camion moyen", [
          { label: "Arrêts / camion", value: summary.avgBusinessStopCountLabel },
          { label: "Temps assigné moyen", value: summary.avgAssignedDurationLabel },
        ])} />
      <CompactMetricTile label="Km / camion" value={summary.avgDistancePerUsedTruckLabel} note="Camion utilisé" />
      <CompactMetricTile label="Voyages / camion actif" value={summary.avgDeliveryVisitCountPerActiveTruckLabel} note="Visites de livraison"
        hoverCard={analyticsHint(period, summary, "Voyages / camion actif", [
          { label: "Visites physiques", value: formatCountLabel(summary.deliveryVisitCount) },
          { label: "Camions actifs (Σ)", value: formatCountLabel(summary.totalActiveTruckCount) },
          { label: "Formule", value: "visites physiques de livraison / camions actifs" },
        ], resolvePlannedAnalyticsNote(period, summary))} />
    </div>
    <div className="dashboard-meters">
      <MetricMeterRow label="Part des km chargés (≥25 %)" value={summary.loadedDistancePctLabel} ratio={chartRatio(summary.loadedDistancePct)}
        note="Complément des km à vide / faible charge"
        hoverCard={analyticsHint(period, summary, "Part des km chargés (≥25 %)", [
          { label: "Km classifiés", value: summary.classifiedDistanceLabel },
          { label: "Part faible charge", value: summary.lowLoadDistancePctLabel },
          { label: "Formule", value: "100 % − part des km à vide / faible charge" },
        ], resolveLowLoadCoverageLabel(period, summary))} />
      <MetricMeterRow label="Part des km à vide / faible charge (<25 %)" value={summary.lowLoadDistancePctLabel} ratio={chartRatio(summary.lowLoadDistancePct)}
        hoverCard={{ title: `${period.label} · Part des km à vide / faible charge (<25 %)`, content: lowLoadHoverContent }} />
    </div>
    <p className="dashboard-card-note">Km et tonnes : camions réellement utilisés · Voyages : tous les camions actifs.</p>
  </DashboardPanel>;
}

function DispatchMixCard({ period = EMPTY_PERIOD, summary }) {
  const currentDay = resolveCurrentPeriodDay(period);
  const metrics = [
    { label: "Préchargement des camions", pct: summary.preloadTruckPct, value: summary.preloadTruckLabel,
      count: currentDay?.preloadTruckCount, total: currentDay?.activeTruckCount, base: "camions" },
    { label: "Heure + priorité", pct: summary.priorityWindowTruckPct, value: summary.priorityWindowTruckLabel,
      count: currentDay?.priorityWindowTruckCount, total: currentDay?.activeTruckCount, base: "camions" },
    { label: "Commandes protégées", pct: summary.protectedOrderPct, value: summary.protectedOrderLabel,
      count: currentDay?.protectedOrderCount, total: currentDay?.protectedOrderTotalCount, base: "commandes" },
  ];
  return <DashboardPanel title="Structure du dispatch" period={period}>
    <div className="dashboard-kpis dashboard-kpis-single"><CompactMetricTile label={period.key === "day" ? "Coût du dispatch" : "Coût moyen par dispatch"} value={summary.realCostPerDispatchLabel} /></div>
    <div className="dashboard-meters">
      {metrics.map((metric) => <MetricMeterRow key={metric.label} label={metric.label} value={metric.value} ratio={chartRatio(metric.pct)}
        note={period.key === "day" ? `${formatCountLabel(metric.count || 0)} / ${formatCountLabel(metric.total || 0)} ${metric.base}` : null}
        hoverCard={analyticsHint(period, summary, metric.label, [
          { label: metric.label, value: metric.value },
          { label: "Camions utilisés", value: formatCountLabel(summary.usedTruckCount) },
          { label: "Camions actifs", value: formatCountLabel(summary.activeTruckCount) },
          { label: "Commandes / dispatch", value: summary.avgDispatchOrderCountLabel },
        ])} />)}
    </div>
    <div className="dashboard-support dashboard-support-three">
      <CompactMetricTile label="Camions utilisés" value={formatCountLabel(summary.usedTruckCount)} note={`${formatCountLabel(summary.activeTruckCount)} actifs`} />
      <CompactMetricTile label="Commandes / dispatch" value={summary.avgDispatchOrderCountLabel} />
      <CompactMetricTile label="Produits" value={summary.dispatchProductCountLabel} />
    </div>
    <p className="dashboard-card-note">{period.key === "day" ? "Ratios du dispatch." : "Moyennes par dispatch actif."} Pourcentages rapportés aux camions actifs ou aux commandes visibles.</p>
  </DashboardPanel>;
}

const DASHBOARD_COMPONENTS = {
  FleetUtilizationCard, MiniLoadPerformanceCard, TrendCard, TonnageCard,
  CompartmentsOverviewCard, MiniClientStopsCard, TruckAverageCard, DispatchMixCard,
};

export function DashboardIndicator({ indicatorKey, ...props }) {
  const definition = DASHBOARD_REPORT_BY_KEY[indicatorKey];
  const Component = definition && DASHBOARD_COMPONENTS[definition.component];
  if (!Component) throw new Error(`Indicateur non enregistré : ${indicatorKey}`);
  return <DashboardIndicatorContext.Provider value={indicatorKey}><Component {...props} /></DashboardIndicatorContext.Provider>;
}

// Report values use the very same formatters as the visible cards.
export function formatDashboardReportMetric(value, unit = "") {
  if (value == null || !Number.isFinite(Number(value))) return "n/d";
  if (unit === "min") return formatDurationLabel(value);
  if (unit === "%") return formatPercentLabel(value);
  if (unit === "t") return formatTonnageLabel(value);
  if (unit === "km") return formatDistanceLabel(value);
  if (unit === "$ CA") return `${formatCurrencyLabel(value)} CA`;
  return `${formatCountLabel(value)}${unit ? ` ${unit}` : ""}`;
}

function DashboardHomeRequestState({ errorMessage = "", onRetry = undefined, status = "loading" }) {
  if (status !== "error") {
    return (
      <div className={`mx-auto min-w-0 w-full px-4 ${DASHBOARD_SURFACE_WIDTH_CLASS_NAME}`} data-dashboard-home-state={status}>
        <AppLoadingBanner />
      </div>
    );
  }

  return (
    <section
      className={`mx-auto flex min-h-64 min-w-0 w-full ${DASHBOARD_SURFACE_WIDTH_CLASS_NAME} items-center justify-center px-4 py-8`}
      data-dashboard-home-state={status}
    >
      <div className="relative grid w-full max-w-2xl place-items-center gap-4 rounded-[8px] border border-[rgba(90,110,98,0.14)] bg-white/94 px-6 py-9 text-center shadow-[0_14px_36px_rgba(41,50,46,0.06)]">
        <div className="pointer-events-none absolute right-4 top-4">
          <BrandLogo size="sm" />
        </div>
        <div className="grid max-w-xl gap-2">
          <p className="text-[0.62rem] font-black uppercase tracking-[0.18em] text-danger">
            Chargement interrompu
          </p>
          <h1 className="text-[1.2rem] font-semibold tracking-[-0.03em] text-slate-950">
            Les indicateurs n’ont pas pu être chargés
          </h1>
          <p className="text-sm leading-6 text-muted-strong">
            {errorMessage || "Le serveur n’a pas renvoyé les données attendues."}
          </p>
        </div>
        <button
          className="inline-flex min-h-10 items-center justify-center rounded-full border border-border-accent-subtle bg-accent px-4 py-2 text-sm font-semibold text-white transition hover:bg-accent-strong"
          type="button"
          onClick={() => void onRetry?.()}
        >
          Réessayer
        </button>
      </div>
    </section>
  );
}

export default function DashboardHome({ initialReadModel = null }) {
  const {
    errorMessage,
    isLoading,
    loadMonth,
    model,
    monthModel,
    monthStatus,
    retry,
    status,
  } = useDashboardHomeData(initialReadModel);
  const [selectedPeriodKey, setSelectedPeriodKey] = React.useState(null);
  const [view, setView] = React.useState("overview");

  if (isLoading) {
    return <DashboardHomeRequestState status="loading" />;
  }

  if (status === "error") {
    return <DashboardHomeRequestState errorMessage={errorMessage} onRetry={retry} status="error" />;
  }

  // The default request holds « Hier » and the last complete week; « 30 dispatchs » is loaded when its tab opens.
  const periodKey = selectedPeriodKey || model.analytics.defaultPeriodKey;
  const periodModel = periodKey === "month" ? monthModel : model;
  const analytics = (periodModel || model).analytics;
  const selectedPeriod = analytics.periods[periodKey] || EMPTY_PERIOD;
  const selectedPeriodSummary = buildPeriodAnalyticsSummary(selectedPeriod, analytics.current);
  const previousSummary = selectedPeriod.previous ? buildPeriodAnalyticsSummary(selectedPeriod.previous, null) : null;
  const contextDays = periodKey === "day" ? analytics.dayContextDays : analytics.last30Days;

  return (
    <DashboardReportContext.Provider value={{ period: periodKey, date: (periodKey === "week" && model.analytics.weekEnd) || model.analytics.current?.dispatchDate, dispatchPeriod: "day" }}>
    <div data-mobile-workspace="dashboard" className={`dashboard-page mx-auto grid min-w-0 w-full ${DASHBOARD_SURFACE_WIDTH_CLASS_NAME} gap-1 px-4 pb-1.5 pt-0 max-[480px]:gap-2`} data-dashboard-mobile-surface="true">
      <WorkspacePageHeader className="dashboard-hero" icon={LayoutDashboard} titleAs="h1" title="Tableau de bord"
        description={<OptimizationStatusLine summary={model.lastOptimizationSummary} />} actions={(
          <div className="flex min-w-0 flex-wrap items-center gap-1.5 xl:justify-end max-[480px]:grid max-[480px]:w-full max-[480px]:grid-cols-1 max-[480px]:items-stretch">
            <div className="dashboard-view-tabs"><PeriodTabs label="Vue" options={VIEW_OPTIONS} value={view} onChange={setView} /></div>
            <PeriodTabs
              onChange={(key) => { setSelectedPeriodKey(key); if (key === "month") void loadMonth(); }}
              options={model.analytics.periodOptions}
              value={periodKey}
            />
            <DashboardReportActions keys={PAGE_REPORT_KEYS} label="Envoyer" />
          </div>
      )} />
      {periodModel ? <div className="dashboard-body" data-view={view}>
      <DashboardKpiStrip period={selectedPeriod} summary={selectedPeriodSummary} previousSummary={previousSummary} contextDays={contextDays} />
      <div className="dashboard-main-row">
        <DashboardIndicator indicatorKey="utilization" compact contextDays={contextDays} period={selectedPeriod} summary={selectedPeriodSummary} />
        <DashboardWatchList period={selectedPeriod} summary={selectedPeriodSummary} previousSummary={previousSummary} />
      </div>
      <div className="dashboard-overview-row">
        <DashboardEconomicsCard period={selectedPeriod} contextDays={contextDays} />
        <TrendChartPanel indicatorKey="tonnage" period={selectedPeriod} summary={selectedPeriodSummary} contextDays={contextDays} />
        <TrendChartPanel indicatorKey="peak_load" period={selectedPeriod} summary={selectedPeriodSummary} contextDays={contextDays} />
      </div>
      <DashboardMetricList period={selectedPeriod} summary={selectedPeriodSummary} previousSummary={previousSummary} />
      </div> : <p className="dashboard-page-status" role="status">{monthStatus === "error" ? <>Les 30 derniers dispatchs n’ont pas pu être chargés.{" "}
        <button type="button" className="rounded-[4px] border border-slate-300 bg-white px-2 py-0.5 font-semibold text-slate-700 hover:border-slate-400 hover:bg-slate-50" onClick={() => void loadMonth()}>Réessayer</button></>
        : "Chargement des 30 derniers dispatchs…"}</p>}
    </div>
    </DashboardReportContext.Provider>
  );
}
